import { FormDatePicker, FormInput, FormSelect } from "@/components";
import useFilter from "./useFilter";
import { FormProvider } from "react-hook-form";
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui";
import {
  invoicestatusOptions,
  YEAR,
} from "../consts";
import { yearOptions, monthOptions } from "@/lib/utils";
import { X } from "lucide-react";
import { FC } from "react";
import { FilterProps } from "./types";
import { MONTH, 
  SCHOOL_NAME, GEO_REGION_ID,
  CRM_RH,
  CRM_RM,
  INVOICE_OWNER, STATUS,
  DUE_DAYS,
  INVOICE_GENERATION_DATE

} from "../consts";
import { taskStatus } from "@/lib/utils";
import { useGetSchoolsQuery } from "@/services";
import { useGeoRegionsQuery } from "@/services/masterData";
import useEmployeesQuery from "@/services/employeeManagement/useEmployeeManagementMutation";
import { LOGIN_ROLES } from "@/lib/utils";

const Filter: FC<FilterProps> = ({
  onCancel,
  filterData,
  setFilterData,
  onApply,
}) => {
  const { form, onSubmit, requiredFields, isMobile, onClear } = useFilter({
    filterData,
    setFilterData,
    onCancel,
    onApply,
  });
  const { errors } = form.formState;

  const { data: geoRes, isLoading: isLoadinggeoreg } = useGeoRegionsQuery({});

  const statusOptions = Object.values(taskStatus).map((s) => ({
    label: s.label,
    value: s.value,
  }));

  const { data: schoolRes, isLoading: isLoadingSchool } = useGetSchoolsQuery(
    {}
  );

  const schoolOptions =
    schoolRes?.data?.[0]?.records?.map((item: any) => ({
      label: item.schoolName,
      value: item.schoolName,
    })) || [];

  const regions =
    geoRes?.data?.[0]?.records?.map((item: any) => ({
      label: item.name,
      value: item._id,
    })) || [];

  

  const { data: rhRes } = useEmployeesQuery({
  role: LOGIN_ROLES.REGION_HEAD,
  });

  const { data: rmRes } = useEmployeesQuery({
    role: LOGIN_ROLES.RELATIONSHIP_MANAGER,
  });

  const rhOptions =
  rhRes?.data?.[0]?.records?.map((item: any) => ({
    label: item.name,
    value: item.name,
  })) || [];

const rmOptions =
  rmRes?.data?.[0]?.records?.map((item: any) => ({
    label: item.name,
    value: item.name,
  })) || [];


  return (
    <Drawer open={true} direction={isMobile ? "bottom" : "right"}>
      <DrawerContent className="px-6">
        <DrawerHeader className="px-0">
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-[20px]">Filter</DrawerTitle>
            <X
              onClick={onCancel}
              className="cursor-pointer stroke-g-300 hover:stroke-g-400"
            />
          </div>
        </DrawerHeader>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
            <div className="bg-white rounded-lg py-[25px] max-h-[70vh] lg:max-h-[95vh] overflow-y-auto pb-[130px] px-1">
              <div className="grid grid-cols-1 gap-[25px]">
                <FormSelect
                  name={YEAR}
                  control={form.control}
                  label="Year"
                  placeholder="Enter year"
                  type="number"
                  error={errors[YEAR]?.message}
                  isRequired={requiredFields.includes(YEAR)}
                  options={yearOptions()}
                />
                {/* Month */}
                <FormSelect
                  name={MONTH}
                  control={form.control}
                  label="Month"
                  placeholder="Please select"
                  error={errors[MONTH]?.message}
                  isRequired={requiredFields.includes(MONTH)}
                  options={monthOptions}
                />

                {/* Region */}
                <FormSelect
                  name={GEO_REGION_ID}
                  control={form.control}
                  label="Region"
                  placeholder="Please select"
                  error={errors[GEO_REGION_ID]?.message}
                  isRequired={requiredFields.includes(GEO_REGION_ID)}
                  options={regions}
                />

                {/* School Name (string) */}
                <FormSelect
                  name={SCHOOL_NAME}
                  control={form.control}
                  label="School Name"
                  placeholder="Please select"
                  error={errors[SCHOOL_NAME]?.message}
                  isRequired={requiredFields.includes(SCHOOL_NAME)}
                  options={schoolOptions}
                />

                {/* CRM RH */}
                <FormSelect
                  name={CRM_RH}
                  control={form.control}
                  label="CRM RH"
                  placeholder="Please select"
                  error={errors[CRM_RH]?.message}
                  isRequired={requiredFields.includes(CRM_RH)}
                  options={rhOptions}
                />

                {/* CRM RM */}
                <FormSelect
                  name={CRM_RM}
                  control={form.control}
                  label="CRM RM"
                  placeholder="Please select"
                  error={errors[CRM_RM]?.message}
                  isRequired={requiredFields.includes(CRM_RM)}
                  options={rmOptions}
                />

                {/* Invoice Owner */}
                <FormInput
                  name={INVOICE_OWNER}
                  control={form.control}
                  label="Invoice Owner"
                  placeholder="Please select"
                  error={errors[INVOICE_OWNER]?.message}
                  isRequired={requiredFields.includes(INVOICE_OWNER)}
                  // options={}
                />

                {/* Status */}
                <FormSelect
                  name={STATUS}
                  control={form.control}
                  label="Status"
                  placeholder="Please select"
                  error={errors[STATUS]?.message}
                  isRequired={requiredFields.includes(STATUS)}
                  options={invoicestatusOptions}
                />

                {/* Due Days */}
                <FormInput
                  name={DUE_DAYS}
                  control={form.control}
                  label="Due Days"
                  placeholder="Enter Due Days"
                  error={errors[DUE_DAYS]?.message}
                  isRequired={requiredFields.includes(DUE_DAYS)}
                />

                {/* Invoice Generation Date */}
                <FormDatePicker
                  name={INVOICE_GENERATION_DATE}
                  control={form.control}
                  label="Invoice Generation Date"
                  placeholder="Select date"
                  error={errors[INVOICE_GENERATION_DATE]?.message}
                  isRequired={requiredFields.includes(INVOICE_GENERATION_DATE)}
                />
              </div>
            </div>
            <div className="flex flex-col gap-3 absolute bottom-3 w-full bg-white pb-[25px] z-[3]">
              <div className="flex justify-end">
                <Button
                  variant="ghost"
                  type="button"
                  className="text-red-600"
                  onClick={onClear}
                >
                  Clear All
                </Button>
              </div>
              <Button type="submit">Apply Filter</Button>
            </div>
          </form>
        </FormProvider>
      </DrawerContent>
    </Drawer>
  );
};

export default Filter;
